import React from 'react';
import { FormattedMessage } from 'react-intl';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, Divider, Typography } from '@mui/material';
import type { Participation, ParticipationType } from 'app/types';
import type { UseQueryRefetch } from 'app/types/useQuery.types';
import { HasAccessTo, ActionButton, ConfirmDialog } from 'app/components';
import { EMPLOYEE } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';
import { ParticipationTranslationKeys } from './EmployeeIncomeParticipations.types';
import {
  EmployeeIncomeParticipationFormDialog,
  ParticipationsLoading,
  ParticipationsEmpty,
  ParticipationsBody,
} from './components';
import { useParticipationsState, useParticipationsDeleteHandler } from './hooks';

type EmployeeIncomeParticipationsProps = {
  loading: boolean;
  connectionId: string | undefined;
  employeeId: string | undefined;
  participations: Participation[];
  refetchParticipations: UseQueryRefetch;
  availableTypes: ParticipationType[];
  title: string;
};

export const EmployeeIncomeParticipations = ({
  loading,
  connectionId,
  employeeId,
  participations,
  refetchParticipations,
  availableTypes,
  title,
}: EmployeeIncomeParticipationsProps) => {
  const disabledIfNoAccessToEmployeeEdit = !HasAccessTo(EMPLOYEE, EDIT);

  const {
    formDialogState,
    deleteDialogState,
    participationToEdit,
    participationToDelete,
    expanded,
    onOpenFormDialog,
    onCloseFormDialog,
    onOpenDeleteDialog,
    onCloseDeleteDialog,
    toggleDetailsPanel,
  } = useParticipationsState(participations);

  const { onConfirmDelete } = useParticipationsDeleteHandler({
    connectionId,
    employeeId,
    participationToDelete,
    refetchParticipations,
    onCloseDeleteDialog,
  });

  const onClickAddButton = React.useCallback(() => {
    onOpenFormDialog();
  }, [onOpenFormDialog]);

  const onClickEditButton = React.useCallback(
    (participation: Participation) => () => {
      onOpenFormDialog(participation);
    },
    [onOpenFormDialog],
  );

  const onClickDeleteButton = React.useCallback(
    (participation: Participation) => () => {
      onOpenDeleteDialog(participation);
    },
    [onOpenDeleteDialog],
  );

  return (
    <>
      {formDialogState && (
        <EmployeeIncomeParticipationFormDialog
          open={formDialogState}
          closeDialog={onCloseFormDialog}
          connectionId={connectionId}
          employeeId={employeeId}
          participation={participationToEdit}
          refetchParticipations={refetchParticipations}
          availableTypes={availableTypes}
        />
      )}

      {deleteDialogState && (
        <ConfirmDialog
          title={<FormattedMessage id={ParticipationTranslationKeys.confirmDeleteTitle} />}
          open={deleteDialogState}
          onConfirm={onConfirmDelete}
          onClose={onCloseDeleteDialog}
        >
          <FormattedMessage id={ParticipationTranslationKeys.confirmDeleteMessage} />
        </ConfirmDialog>
      )}

      <Box sx={{ width: '100%' }}>
        <Divider sx={{ borderColor: '#c5d9e299', borderWidth: 0.25, my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="overline">
            <FormattedMessage id={title} />
          </Typography>
          <ActionButton
            messageId={ParticipationTranslationKeys.addButton}
            variant="text"
            icon={faPlus}
            onClick={onClickAddButton}
            disabled={disabledIfNoAccessToEmployeeEdit}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          {loading && <ParticipationsLoading />}

          {participations.length === 0 ? (
            <ParticipationsEmpty />
          ) : (
            <ParticipationsBody
              participations={participations}
              expanded={expanded}
              loading={loading}
              disabledEditButton={disabledIfNoAccessToEmployeeEdit}
              disabledDeleteButton={disabledIfNoAccessToEmployeeEdit}
              onExpand={toggleDetailsPanel}
              onEditButtonClick={onClickEditButton}
              onDeleteButtonClick={onClickDeleteButton}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
